import React, {useEffect, useRef, useState} from 'react';
import {Animated, StyleSheet, View, ViewStyle} from 'react-native';

import GiftCardStoreCard, {
  CAPABILITY_CHIPS_COL_WIDTH,
  DISTANCE_COL_WIDTH,
  SHOW_ONLINE_SHOPPING_OPTIONS,
} from '../../components/GiftCardStoreCard';
import {Icon} from '../../components/Icon';
import SuburbAutocomplete from '../../components/SuburbAutocomplete';
import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import ActivityIndicator from '../../components/ui-controls/ActivityIndicator';
import {fontFamilyStyle} from '../../components/ui-controls/Text';
import {config} from '../../config';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {GeocodedSuburb, Shop} from '../../models/middleware';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {getBrandDisplayName} from '../../utils/brandConfig';
import {
  filterGiftCardStores,
  navigateToGiftCardsShop,
  sortGiftCardStoresByDistance,
} from '../../utils/giftCards';
import {logger, serializeError} from '../../utils/logger';

const MAX_DISTANCE_KM = 200;
// Auto-dismiss inline notification banners ("Awesome…", "no stores…",
// "no gift card stores…", "Something went wrong…") after this delay so they
// don't linger over the results panel indefinitely.
const DEFAULT_BANNER_AUTO_DISMISS_MS = 5000;
// Subtle fade + lift animation timings (ms) for banner enter/exit.
const BANNER_FADE_IN_MS = 180;
const BANNER_FADE_OUT_MS = 220;
const PRIMARY = theme.colour.text.heading?.primary ?? '#0054A6';

type Props = {
  navigation: RouteNavigatorScreenProps<AppRoute.PROMOTION>['navigation'];
  isRootStoreSelectionFlow?: boolean;
  // Override the auto-dismiss timeout. Pass 0 to disable auto-dismiss.
  bannerAutoDismissMs?: number;
  // Hide the "Buy a gift card" footer action. The standalone Participating
  // Stores screen sets this — purchase entry points live elsewhere there.
  hideBuyButton?: boolean;
};

export type AvailabilityState =
  | 'idle'
  | 'success'
  | 'no-stores-in-area'
  | 'no-gift-card-stores'
  | 'error';

export type GiftCardPromotionExtraContentViewProps = {
  isLoading: boolean;
  shopList: Shop[];
  availability: AvailabilityState;
  bannerVisible: boolean;
  onSelectSuburb: (suburb: GeocodedSuburb) => void;
  onTermsAndConditionsPress: () => void;
  onBuyGiftCardPress: () => void;
  hideBuyButton?: boolean;
};

/**
 * Banner shell with a subtle fade + 4px lift animation on exit and a
 * matching fade-in on enter. Stays mounted while exit animation runs, then
 * unmounts via a deterministic setTimeout (so tests can flush via
 * `jest.advanceTimersByTime` without depending on requestAnimationFrame).
 */
type AnimatedBannerProps = {
  visible: boolean;
  style?: ViewStyle | ViewStyle[];
  testID?: string;
  children: React.ReactNode;
};

const AnimatedBanner = ({visible, style, testID, children}: AnimatedBannerProps) => {
  const opacity = useRef(new Animated.Value(visible ? 1 : 0)).current;
  const translateY = useRef(new Animated.Value(visible ? 0 : -4)).current;
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      const anim = Animated.parallel([
        Animated.timing(opacity, {
          toValue: 1,
          duration: BANNER_FADE_IN_MS,
          useNativeDriver: true,
        }),
        Animated.timing(translateY, {
          toValue: 0,
          duration: BANNER_FADE_IN_MS,
          useNativeDriver: true,
        }),
      ]);
      anim.start();
      return () => anim.stop();
    }
    const anim = Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        duration: BANNER_FADE_OUT_MS,
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: -4,
        duration: BANNER_FADE_OUT_MS,
        useNativeDriver: true,
      }),
    ]);
    anim.start();
    const t = setTimeout(() => setMounted(false), BANNER_FADE_OUT_MS);
    return () => {
      anim.stop();
      clearTimeout(t);
    };
  }, [visible, opacity, translateY]);

  if (!mounted) return null;
  return (
    <Animated.View style={[style, {opacity, transform: [{translateY}]}]} testID={testID}>
      {children}
    </Animated.View>
  );
};

/**
 * Presentational view — receives all data via props. No state, no store
 * access. Use this directly (with mock props) for storybook / static
 * previews. The default export below wraps this with state + store wiring.
 */
export const GiftCardPromotionExtraContentView = ({
  isLoading,
  shopList,
  availability,
  bannerVisible,
  onSelectSuburb,
  onTermsAndConditionsPress,
  onBuyGiftCardPress,
  hideBuyButton = false,
}: GiftCardPromotionExtraContentViewProps) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const visibleStores = shopList;
  const successfulCount = visibleStores.length;
  const brandName = getBrandDisplayName();

  return (
    <View style={styles.root}>
      {/* Hero header */}
      <View style={styles.heroHeader}>
        <Text style={styles.heroEyebrow}>FIND A STORE</Text>
        <Text style={styles.heroTitle}>CHECK PARTICIPATING STORES NEAR YOU</Text>
        <Text style={styles.heroSubtitle}>
          Search by suburb or postcode to confirm there&apos;s a {brandName} store near your gift
          card recipient.
        </Text>
      </View>

      {/* Search panel */}
      <View style={styles.searchPanel}>
        <Text style={styles.searchLabel}>CHECK STORE LOCATION</Text>
        <SuburbAutocomplete
          placeholder="Enter your postcode or suburb"
          onSelectSuburb={onSelectSuburb}
        />

        {isLoading ? (
          <View style={styles.loadingRow}>
            <ActivityIndicator size="small" />
            <Text style={styles.loadingText}>Looking for stores...</Text>
          </View>
        ) : null}

        <AnimatedBanner
          visible={Boolean(!isLoading && bannerVisible && availability === 'success')}
          style={[styles.banner, styles.bannerSuccess]}
          testID="availability-banner-success">
          <Icon name="circle-check" size={14} color="#047857" />
          <Text style={[styles.bannerText, styles.bannerSuccessText]}>
            Awesome, we got you covered
          </Text>
        </AnimatedBanner>

        <AnimatedBanner
          visible={Boolean(!isLoading && bannerVisible && availability === 'no-stores-in-area')}
          style={[styles.banner, styles.bannerWarn]}
          testID="availability-banner-empty">
          <Icon name="circle-exclamation" size={14} color="#B45309" />
          <Text style={styles.bannerText}>
            Sorry, there are no stores currently servicing this area.
          </Text>
        </AnimatedBanner>

        <AnimatedBanner
          visible={Boolean(!isLoading && bannerVisible && availability === 'no-gift-card-stores')}
          style={[styles.banner, styles.bannerWarn]}
          testID="availability-banner-no-cards">
          <Icon name="circle-exclamation" size={14} color="#B45309" />
          <Text style={styles.bannerText}>
            {`Sorry, no nearby ${brandName} stores are currently selling Gift Cards in this area.`}
          </Text>
        </AnimatedBanner>

        <AnimatedBanner
          visible={Boolean(!isLoading && bannerVisible && availability === 'error')}
          style={[styles.banner, styles.bannerError]}
          testID="availability-banner-error">
          <Icon name="circle-exclamation" size={14} color="#B91C1C" />
          <Text style={[styles.bannerText, styles.bannerErrorText]}>
            Something went wrong looking up stores. Please try again.
          </Text>
        </AnimatedBanner>
      </View>

      {/* Results list */}
      {!isLoading && availability === 'success' && visibleStores.length > 0 && (
        <View style={styles.resultsPanel} testID="gift-card-results-panel">
          <View style={styles.resultsHeader}>
            <View style={styles.resultsHeaderLeft}>
              <Icon name="location-dot" size={12} color={PRIMARY} />
              <Text style={styles.resultsHeaderTitle}>PARTICIPATING STORES NEAR YOU</Text>
              <Text style={styles.resultsHeaderDivider}>|</Text>
              <Text style={styles.resultsHeaderCount}>
                {`${successfulCount} ${successfulCount === 1 ? 'STORE' : 'STORES'} FOUND`}
              </Text>
            </View>
            {/* Column labels are desktop-only; mobile uses card-internal layout.
                Widths mirror the card's fixed columns so labels sit directly
                above their corresponding column data. */}
            {isWebDesktopScreen ? (
              <>
                <Text
                  style={[styles.resultsHeaderColumn, {width: DISTANCE_COL_WIDTH}]}
                  testID="results-header-col-distance">
                  DISTANCE
                </Text>
                {SHOW_ONLINE_SHOPPING_OPTIONS ? (
                  <Text
                    style={[styles.resultsHeaderColumn, {width: CAPABILITY_CHIPS_COL_WIDTH}]}
                    testID="results-header-col-fulfillment">
                    ONLINE SHOPPING OPTIONS
                  </Text>
                ) : null}
              </>
            ) : null}
          </View>
          {/* Plain View + map (not FlatList) so this list never nests a
              VirtualizedList inside the parent ScrollView — that nesting
              breaks tap forwarding on native. Outer ScrollView handles
              scrolling for the full result set. */}
          <View>
            {visibleStores.map((item, index) => (
              <React.Fragment key={item.store_number}>
                {index > 0 ? <View style={styles.cardDivider} /> : null}
                <GiftCardStoreCard item={item} />
              </React.Fragment>
            ))}
          </View>
        </View>
      )}

      {/* Footer actions: row + space-between on desktop, stacked + centered on mobile */}
      <View style={[styles.footer, !isWebDesktopScreen && styles.footerMobile]}>
        <Button
          containerStyle={
            isWebDesktopScreen ? styles.termsButtonContainer : styles.termsButtonContainerMobile
          }
          onPress={onTermsAndConditionsPress}
          title="Terms & Conditions"
          titleStyle={styles.termsButtonTitle}
          type="clear"
        />
        {!hideBuyButton && (
          <Button
            containerStyle={
              isWebDesktopScreen ? styles.buyButtonContainer : styles.buyButtonContainerMobile
            }
            buttonStyle={styles.buyButton}
            titleStyle={styles.buyButtonTitle}
            onPress={onBuyGiftCardPress}
            icon={<Icon name="gift" size={14} color="#FFFFFF" />}
            iconPosition="left"
            title="Buy a gift card"
          />
        )}
      </View>
    </View>
  );
};

/**
 * Container — owns state and store wiring, delegates rendering to the View.
 */
const GiftCardPromotionExtraContent = ({
  navigation,
  isRootStoreSelectionFlow = false,
  bannerAutoDismissMs = DEFAULT_BANNER_AUTO_DISMISS_MS,
  hideBuyButton = false,
}: Props) => {
  const rootStore = useStores();
  const {shopSelectionStore} = rootStore;

  const [isLoading, setIsLoading] = useState(false);
  const [shopList, setShopList] = useState<Shop[]>([]);
  const [availability, setAvailability] = useState<AvailabilityState>('idle');
  // Drives banner visibility independently of `availability` so the results
  // panel (gated on availability === 'success') survives the banner timing
  // out. Reset on every new lookup so the latest result re-surfaces its
  // banner even when the underlying availability state doesn't change.
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    if (
      isLoading ||
      availability === 'idle' ||
      !bannerVisible ||
      !bannerAutoDismissMs ||
      bannerAutoDismissMs <= 0
    ) {
      return;
    }
    const t = setTimeout(() => setBannerVisible(false), bannerAutoDismissMs);
    return () => clearTimeout(t);
  }, [availability, isLoading, bannerVisible, bannerAutoDismissMs]);

  const handleTermsAndConditionsPress = () => {
    const target = isRootStoreSelectionFlow
      ? AppRoute.ROOT_GIFT_CARDS_TERMS_AND_CONDITIONS
      : AppRoute.GIFT_CARDS_TERMS_AND_CONDITIONS;
    navigation.navigate(target);
  };

  const handleSelectSuburb = async (suburb: GeocodedSuburb) => {
    setIsLoading(true);
    setAvailability('idle');
    setBannerVisible(false);
    try {
      // DIS-1501-B: flag ON → full tenant list (all stores, closest first,
      // delivery enriched by searched suburb; safety-capped at MAX_PAGES×limit).
      // Flag OFF → legacy suburb-search path.
      const allStores = config.FF_GIFT_CARD_ALL_STORES
        ? await shopSelectionStore.queryAllTenantStores(suburb)
        : await shopSelectionStore.querySuburbStores(suburb, MAX_DISTANCE_KM);
      // Fulfillment chips use the same search-row flags as HomePage StoreNameCard /
      // FIND YOUR STORE (pickup_available + suburb-scoped delivery_available).
      const giftCardStores = sortGiftCardStoresByDistance(filterGiftCardStores(allStores));
      setShopList(giftCardStores);
      if (allStores.length === 0) {
        setAvailability('no-stores-in-area');
      } else if (giftCardStores.length === 0) {
        setAvailability('no-gift-card-stores');
      } else {
        setAvailability('success');
      }
      setBannerVisible(true);
    } catch (err) {
      logger.error(
        'GiftCardPromotionExtraContent',
        'querySuburbStores failed',
        serializeError(err),
      );
      setShopList([]);
      setAvailability('error');
      setBannerVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GiftCardPromotionExtraContentView
      isLoading={isLoading}
      shopList={shopList}
      availability={availability}
      bannerVisible={bannerVisible}
      onSelectSuburb={handleSelectSuburb}
      onTermsAndConditionsPress={handleTermsAndConditionsPress}
      onBuyGiftCardPress={() => navigateToGiftCardsShop(rootStore)}
      hideBuyButton={hideBuyButton}
    />
  );
};

export default GiftCardPromotionExtraContent;

const styles = StyleSheet.create({
  root: {
    width: '100%',
    paddingVertical: 16,
    gap: 16,
  },

  // Hero
  heroHeader: {
    alignItems: 'center',
    paddingHorizontal: 16,
    gap: 4,
  },
  heroEyebrow: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6B7280',
    letterSpacing: 1.5,
  },
  heroTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: PRIMARY,
    textAlign: 'center',
    letterSpacing: 0.5,
    marginTop: 2,
  },
  heroSubtitle: {
    fontSize: 13,
    color: '#4B5563',
    textAlign: 'center',
    marginTop: 4,
    lineHeight: 18,
    maxWidth: 480,
  },

  // Search panel
  searchPanel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    gap: 8,
  },
  searchLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6B7280',
    letterSpacing: 1.2,
    marginBottom: 4,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
  },
  loadingText: {
    fontSize: 13,
    color: '#4B5563',
  },

  // Banner
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  bannerSuccess: {
    backgroundColor: '#D1FAE5',
  },
  bannerWarn: {
    backgroundColor: '#FEF3C7',
  },
  bannerError: {
    backgroundColor: '#FEE2E2',
  },
  // Aligned with hero subtitle (13px, regular weight) for visual consistency
  // with the rest of the screen's body copy. Success label uses semibold +
  // success-green for emphasis only.
  bannerText: {
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 18,
    color: '#4B5563',
    flexShrink: 1,
  },
  bannerSuccessText: {
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 18,
    color: '#047857',
  },
  bannerErrorText: {
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 18,
    color: '#B91C1C',
  },

  // Results
  resultsPanel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    overflow: 'hidden',
  },
  resultsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    gap: 16,
  },
  resultsHeaderLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  resultsHeaderTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6B7280',
    letterSpacing: 1.2,
  },
  resultsHeaderDivider: {
    fontSize: 11,
    color: '#D1D5DB',
    marginHorizontal: 4,
  },
  resultsHeaderCount: {
    fontSize: 11,
    fontWeight: '700',
    color: PRIMARY,
    letterSpacing: 1.2,
  },
  // Column labels positioned to match the fixed-width columns inside each
  // GiftCardStoreCard so the column labels sit directly above their
  // corresponding data column.
  resultsHeaderColumn: {
    fontSize: 10,
    fontWeight: '700',
    color: '#9CA3AF',
    letterSpacing: 1,
  },
  cardDivider: {
    height: 1,
    backgroundColor: '#F3F4F6',
    marginHorizontal: 16,
  },

  // Footer
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    flexWrap: 'wrap',
    gap: 12,
  },
  // Mobile footer: stacked vertically and centered (T&C above Buy button)
  footerMobile: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 16,
  },
  termsButtonContainer: {
    marginLeft: 0,
  },
  termsButtonContainerMobile: {
    alignSelf: 'center',
    marginHorizontal: 0,
  },
  termsButtonTitle: {
    ...fontFamilyStyle,
    color: PRIMARY,
    fontSize: 13,
    textDecorationLine: 'underline',
  },
  buyButtonContainer: {
    marginRight: 0,
  },
  buyButtonContainerMobile: {
    alignSelf: 'center',
    marginHorizontal: 0,
  },
  buyButton: {
    backgroundColor: PRIMARY,
    borderRadius: 999,
    paddingHorizontal: 24,
    height: 44,
    gap: 8,
  },
  buyButtonTitle: {
    ...fontFamilyStyle,
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
